// ©2024 Austin App House. All rights reserved.
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { toggleModal } from '../../../storage/actions/modalsActions';
import {
  getWorkOrdersForBulk,
  bulkApproveWorkOrders,
} from '../../../storage/actions/workOrdersActions';
import { getAccountBalanceByClient } from '../../../storage/actions/profile';
import Loader from '../../dashboard/common/Loader';
import prepareFilters from '../../../utils/prepareFilters';
import Content from './content';

const BulkWorkOrderApproveModal = ({
  modalState,
  balance,
  selectedWorkOrders,
  toggleModal,
  bulkApproveWorkOrders,
  getWorkOrdersForBulk,
  getAccountBalanceByClient,
  filters,
  search,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [contentType, setContentType] = useState('');
  const [workOrdersInfo, setWorkOrdersInfo] = useState([]);
  const [fundsInfo, setFundsInfo] = useState({
    holdFunds: 0,
    fundsNeededFromWallet: 0,
    expensesNeededFromWallet: 0,
    expenses: 0,
    fundsNeeded: 0,
    fundsExpected: 0,
  });
  const [requestData, setRequestData] = useState({});

  const workOrdersCount = workOrdersInfo.reduce((acc, cur) => acc + cur.count, 0);
  const workOrderCountWithExpenses = workOrdersInfo.reduce(
    (acc, cur) => acc + cur.countWithExpenses,
    0,
  );

  const initRequestData = () => {
    const newRequestData = modalState?.type?.mode === 'useFilters'
      ? {
        ...search,
        filters: prepareFilters(filters),
      }
      : { workOrdersId: selectedWorkOrders };
    setRequestData({ ...newRequestData, status: 'completed' });
    return ({ ...newRequestData, status: 'completed' });
  };

  const fetchData = async () => {
    setIsLoading(true);
    await getAccountBalanceByClient();
    const ordersInfo = await getWorkOrdersForBulk(initRequestData());
    const funds = {
      holdFunds: 0,
      fundsNeededFromWallet: 0,
      expensesNeededFromWallet: 0,
      expenses: 0,
      fundsNeeded: 0,
      fundsExpected: 0,
    };
    ordersInfo.forEach((info) => {
      funds.fundsExpected += info.fundsExpected;
      funds.fundsNeeded += info.fundsNeeded;
      if (!info.payOutsideOfApp) {
        funds.holdFunds += info.holdFunds;
        funds.fundsNeededFromWallet += info.fundsNeeded;
        funds.expensesNeededFromWallet += info.expenses;
      }
      funds.expenses += info.expenses;
    });
    setFundsInfo(funds);
    setWorkOrdersInfo(ordersInfo || []);
    setIsLoading(false);
  };

  useEffect(() => {
    if (!modalState?.isOpen) {
      return undefined;
    }

    fetchData();

    return () => {
      setIsLoading(true);
      setContentType('');
      setWorkOrdersInfo([]);
    };
  }, [modalState?.isOpen]);

  useEffect(() => {
    if (isLoading) return;
    if (fundsInfo.expenses > 0) return setContentType('workOrderHaveExpenses');
    if (fundsInfo.expenses === 0) return setContentType('approveAvailable');
    return setContentType('Error!!!');
  }, [balance, fundsInfo, isLoading]);

  const onClickContinue = () => setContentType('approveAvailable');

  const closeModal = () => toggleModal(false, 'bulkWorkOrderApproveModal');

  const submitApprove = () => {
    if (
      (balance.total_balance - balance.on_hold)
      - (fundsInfo.fundsNeededFromWallet - fundsInfo.holdFunds) < 0
    ) return setContentType('notEnoughFunds');
    return bulkApproveWorkOrders(requestData);
  };

  const submitApproveWorkOrderWithoutExpenses = () => {
    bulkApproveWorkOrders({
      ...requestData,
      approvedExpenses: 'skip',
    });
  };

  const addFunds = () => {
    toggleModal(true, 'addFunds');
  };

  const getModalTitle = () => {
    switch (contentType) {
      case 'workOrderHaveExpenses':
        return `${workOrderCountWithExpenses} Work Orders Contain Expenses`;
      case 'approveAvailable':
        return `${workOrdersCount} Work Order Ready To Approve`;
      case 'notEnoughFunds':
        return 'Not Enough Funds Available!';
      default:
        return 'Approve Work Orders';
    }
  };

  const getModalSubtitle = () => {
    switch (contentType) {
      case 'workOrderHaveExpenses':
        return 'Please review the total expenses before proceeding.';
      case 'approveAvailable':
        return 'Please review the payment breakdown before approving.';
      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (contentType) {
      case 'workOrderHaveExpenses':
        return (
          <>
            <button
              type="button"
              className="standard_btn dark_btn"
              aria-label="cancel"
              onClick={closeModal}
            >
              Close
            </button>
            <button
              type="button"
              className="standard_btn dark_btn"
              aria-label="skip"
              onClick={submitApproveWorkOrderWithoutExpenses}
            >
              Skip
            </button>
            <button
              type="button"
              className="standard_btn light_btn"
              aria-label="continue"
              onClick={onClickContinue}
            >
              Continue
            </button>
          </>
        );
      case 'approveAvailable':
        return (
          <>
            <button
              type="button"
              className="standard_btn dark_btn"
              aria-label="cancel"
              onClick={closeModal}
            >
              Close
            </button>
            <button
              type="button"
              className="standard_btn light_btn"
              aria-label="Submit"
              onClick={submitApprove}
            >
              Approve
            </button>
          </>
        );
      case 'notEnoughFunds':
        return (
          <>
            <button
              type="button"
              className="standard_btn dark_btn"
              aria-label="cancel"
              onClick={closeModal}
            >
              Close
            </button>
            <button
              type="button"
              className="standard_btn light_btn"
              aria-label="add-funds"
              onClick={addFunds}
            >
              Add Funds
            </button>
          </>
        );
      default:
        return (
          <button
            type="button"
            className="standard_btn dark_btn"
            aria-label="cancel"
            onClick={closeModal}
          >
            Close
          </button>
        );
    }
  };

  if (!modalState?.isOpen) {
    return null;
  }

  const subtitle = getModalSubtitle();

  return (
    <div
      className="modal_block dashboard-modal dashboard-modal--centered modal_import showed"
      role="presentation"
      onClick={closeModal}
    >
      <div
        className="modal_container"
        style={{ minHeight: 'fit-content' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-approve-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal_head">
          <div className="modal_title" id="bulk-approve-title">
            {getModalTitle()}
          </div>
          {subtitle && (
            <div className="modal_subtitle">
              <p>{subtitle}</p>
            </div>
          )}
          <button
            type="button"
            className="close_btn icon_close"
            aria-label="close"
            onClick={closeModal}
          />
        </div>
        <div className="modal_body">
          {isLoading || !contentType ? (
            <div className="flex-box flex-center full-width">
              <Loader color="black" />
            </div>
          ) : (
            <Content
              contentType={contentType}
              fundsInfo={fundsInfo}
            />
          )}
        </div>
        {!isLoading && contentType && (
          <div className="modal_footer">
            {renderFooter()}
          </div>
        )}
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  toggleModal,
  bulkApproveWorkOrders,
  getWorkOrdersForBulk,
  getAccountBalanceByClient,
};

const mapStateToProps = (state) => ({
  modalState: state.modalState.bulkWorkOrderApproveModal,
  selectedWorkOrders: state.applicationState.selectedWorkOrders,
  balance: state.profile.balance,
  filters: state.workOrder?.filters,
  search: state.workOrder?.searchData,
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkWorkOrderApproveModal);
